import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Calendar, 
  MapPin, 
  Link as LinkIcon,
  Edit,
  Camera,
  Save,
  X
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';

const Profile: React.FC = () => {
  const { colors } = useTheme();
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    bio: 'Passionate learner exploring the world of technology and innovation.',
    location: 'San Francisco, CA',
    website: 'https://example.com',
    joinedDate: '2024-01-15'
  });

  const stats = [
    { label: 'Notes Created', value: '127', color: colors.blue },
    { label: 'Projects Completed', value: '8', color: colors.green },
    { label: 'Study Hours', value: '156', color: colors.yellow },
    { label: 'AI Conversations', value: '34', color: colors.mauve }
  ];

  const achievements = [
    { 
      id: '1', 
      title: 'First Note', 
      description: 'Created your first note', 
      icon: '📝', 
      earned: true,
      date: '2024-01-15'
    },
    { 
      id: '2', 
      title: 'Study Streak', 
      description: '7 days of continuous learning', 
      icon: '🔥', 
      earned: true,
      date: '2024-01-22'
    },
    { 
      id: '3', 
      title: 'Project Master', 
      description: 'Completed 5 projects', 
      icon: '🏆', 
      earned: true,
      date: '2024-02-01'
    },
    { 
      id: '4', 
      title: 'AI Explorer', 
      description: 'Had 25 AI conversations', 
      icon: '🤖', 
      earned: true,
      date: '2024-02-05'
    },
    { 
      id: '5', 
      title: 'Knowledge Graph', 
      description: 'Connected 50 notes', 
      icon: '🕸️', 
      earned: false,
      date: null
    },
    { 
      id: '6', 
      title: 'Document Vault', 
      description: 'Upload 20 documents', 
      icon: '📚', 
      earned: false,
      date: null
    }
  ];

  const handleSave = () => {
    updateUser({
      name: formData.name,
      email: formData.email
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      ...formData,
      name: user?.name || '',
      email: user?.email || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Profile
        </h1>
        
        {!isEditing ? (
          <button 
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: colors.blue }}
          >
            <Edit className="w-4 h-4" />
            Edit Profile
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button 
              onClick={handleSave}
              className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: colors.green }}
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button 
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg border font-medium transition-all hover:opacity-80 flex items-center gap-2"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Profile Card */}
      <div 
        className="p-8 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex flex-col md:flex-row gap-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center">
            <div className="relative">
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold text-white mb-4"
                style={{ backgroundColor: colors.blue }}
              >
                {formData.name.charAt(0).toUpperCase() || 'U'}
              </div>
              {isEditing && (
                <button 
                  className="absolute bottom-4 right-0 p-2 rounded-full text-white transition-all hover:opacity-80"
                  style={{ backgroundColor: colors.surface0 }}
                >
                  <Camera className="w-4 h-4" style={{ color: colors.text }} />
                </button>
              )}
            </div>
            
            <div className="text-center">
              <span className="text-sm" style={{ color: colors.subtext0 }}>
                Member since {new Date(formData.joinedDate).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                    style={{
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  />
                ) : (
                  <p style={{ color: colors.text }}>{formData.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Email Address
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                    style={{
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  />
                ) : (
                  <p style={{ color: colors.text }}>{formData.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Location
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                    style={{
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" style={{ color: colors.subtext0 }} />
                    <span style={{ color: colors.text }}>{formData.location}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Website
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                    style={{
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" style={{ color: colors.subtext0 }} />
                    <a 
                      href={formData.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                      style={{ color: colors.blue }}
                    >
                      {formData.website}
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                Bio
              </label>
              {isEditing ? (
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors resize-none"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
              ) : (
                <p style={{ color: colors.text }}>{formData.bio}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="p-6 rounded-xl border text-center"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0
            }}
          >
            <div className="text-3xl font-bold mb-2" style={{ color: stat.color }}>
              {stat.value}
            </div>
            <div className="text-sm" style={{ color: colors.subtext0 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Achievements */}
      <div 
        className="p-6 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <h2 className="text-xl font-semibold mb-6" style={{ color: colors.text }}>
          Achievements
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`p-4 rounded-lg border transition-all ${
                achievement.earned ? 'hover:scale-105' : 'opacity-60'
              }`}
              style={{ 
                backgroundColor: colors.base,
                borderColor: achievement.earned ? colors.surface1 : colors.surface0
              }}
            >
              <div className="flex items-start gap-3">
                <div className="text-2xl">{achievement.icon}</div>
                <div className="flex-1">
                  <h3 className="font-medium mb-1" style={{ color: colors.text }}>
                    {achievement.title}
                  </h3>
                  <p className="text-sm mb-2" style={{ color: colors.subtext0 }}>
                    {achievement.description}
                  </p>
                  {achievement.earned && achievement.date && (
                    <div className="flex items-center gap-1 text-xs" style={{ color: colors.subtext0 }}>
                      <Calendar className="w-3 h-3" />
                      {new Date(achievement.date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Profile;